import React from 'react'
import { include, reverse } from 'named-urls'
import { Switch, Redirect, Route } from 'react-router-dom'

import { RouteWithLayout } from './components'

import {
  Auth as AuthLayout,
  MainLayout,
  Portal as PortalLayout,
  JourneyStepsLayout,
  Checkout as CheckoutLayout,
  JourneyStepsWithoutUserBoxLayout,
  JourneyWhiteLabel,
} from './layouts'

import AssetInventoryProvider from 'providers/AssetInventoryProvider'
import CheckoutPaymentProvider from 'providers/CheckoutPaymentProvider'
import ClientPortalThemeProvider from 'providers/ClientPortalThemeProvider'
import CompanyFragilityProvider from 'providers/CompanyFragilityProvider'
import ConsentProvider from 'providers/ConsentProvider'
import CookiesProvider from 'providers/CookiesProvider'
import DataProcessOptionsProvider from 'providers/DataProcessOptionsProvider'
import DataProcessProvider from 'providers/DataProcessProvider'
import DocumentGenerateProvider from 'providers/DocumentGenerateProvider'
import FirstStepProvider from 'providers/FirstStepProvider'
import GovernanceProjectNameDpoProvider from 'providers/GovernanceProjectNameDpoProvider'
import IncidentManegementProvider from 'providers/IncidentManegementProvider'
import PhishingProvider from 'providers/PhishingProvider'
import SubscriptionProvider from 'providers/SubscriptionProvider'
import SuggestedChangeProvider from 'providers/SuggestedChangeProvider'
import SupplierInviteProvider from 'providers/SupplierInviteProvider'
import TrailStepProvider from 'providers/TrailStepProvider'

import useAuth from 'hooks/useAuth'

// import LoginTransparentIntegration from 'views/LoginTransparentIntegration'
import ActivitiesPanel from 'views/ActivitiesPanel'
import AssetInventory from 'views/AssetInventory'
import Audits from 'views/Audits'
import Certificate from 'views/Certificate'
import ChangesManagement from 'views/ChangesManagement'
import Committes from 'views/Committes'
import ExternalPlatformIntegrations from 'views/ExternalPlatformIntegrations'
import CompanyFragilities from 'views/CompanyFragilities'
import Complaint from 'views/Complaint'
import Consent from 'views/Consent'
import Cookies from 'views/Cookies'
import DataIntelligence from 'views/DataIntelligence'
import DataProcesses from 'views/DataProcesses'
import DataProcessTreatment from 'views/DataProcessesTreatment'
import DataSubjectTickets from 'views/DataSubjectTickets'
import Departments from 'views/Departments'
import Documents from 'views/Documents'
import DocumentTemplates from 'views/DocumentTemplates'
import FaqsDocumentations from 'views/FaqsDocumentations'
import Forbidden from 'views/Forbidden'
import ForgotPassword from 'views/ForgotPassword'
import GovernanceProjects from 'views/GovernanceProjects'
import GovernanceProjectStages from 'views/GovernanceProjectStages'
import HelpCenter from 'views/HelpCenter'
import IncidentManagements from 'views/IncidentManagements'
import Incidents from 'views/Incidents'
import Phishing from 'views/Phishing'
import Kpi from 'views/MonitoringManagement/Kpi'
import LastUpdates from 'views/LastUpdates'
import Login, {
  IntegrationLogin,
  MicrosoftLogin,
  LoginSSO,
  LoginSSOError,
} from 'views/Login'
import Marketplace from 'views/Marketplace'
import MyLgpd from 'views/MyLgpd'
import NewPassword from 'views/NewPassword'
import NotFound from 'views/NotFound'
import PdcaCycle from 'views/PdcaCycle'
import Pdca from 'views/Pdca'
import Nc from 'views/MonitoringManagement/Nc'
import Pmc from 'views/MonitoringManagement/Pmc'
import AiInventory from 'views/AiInventory'
import PrivacyByDesign from 'views/PrivacyByDesign'
import PrivacyPortal from 'views/PrivacyPortal'
import PrivacyStamps from 'views/PrivacyStamps/PrivacyStamp'
import Profiles from 'views/Profiles'
import QuestionControls from 'views/QuestionControls'
import Questionnaires from 'views/Questionnaires'
import Reports from 'views/Reports'
import SelectCompany from 'views/SelectCompany'
import Settings from 'views/Settings'
import Subscription, { CheckoutPayment, PaymentLink } from 'views/Subscription'
import SubscriptionCompany from 'views/SubscriptionCompany'
import SupplierInvite from 'views/SupplierInvite'
import SupplierStep from 'views/SupplierStep'
import TechnicalFaqs from 'views/TechnicalFaqs'
import ThirdPartyManagements from 'views/ThirdPartyManagements'
import TrainingCourses from 'views/TrainingCourses'
import TrainningJourney from 'views/TrainningJourney'
import TwoFactorAuthentication from 'views/TwoFactorAuthentication'
import { ServerError } from 'views/UnexpectedError'
import Users from 'views/Users'
import UserSteps from 'views/UserSteps'
import UserTrailSteps from 'views/UserTrailSteps'

import permission from 'constants/permissions'
import constants from 'constants/index'

const Routes = () => {
  return (
    <Switch>
      <RouteWithLayout
        path={routes.selectCompany}
        component={SelectCompany}
        provider={ClientPortalThemeProvider}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.userSteps}
        component={UserSteps.Main}
        layout={JourneyStepsLayout}
        provider={FirstStepProvider}
        auth
      />
      <RouteWithLayout
        path={routes.userTrailSteps}
        component={UserTrailSteps.Main}
        layout={JourneyStepsLayout}
        provider={TrailStepProvider}
        auth
      />
      <RouteWithLayout
        path={routes.trainningJourney}
        component={TrainningJourney.Main}
        layout={JourneyStepsLayout}
        auth
      />
      <RouteWithLayout
        path={routes.supplierStep}
        component={SupplierStep.Main}
        layout={JourneyStepsLayout}
        auth
      />
      <RouteWithLayout
        path={routes.supplierInvite}
        component={SupplierInvite.Main}
        layout={JourneyStepsWithoutUserBoxLayout}
        redirect={false}
        provider={SupplierInviteProvider}
      />
      <RouteWithLayout
        path={routes.consultCertificate}
        component={Certificate.Consult}
        layout={JourneyStepsLayout}
      />
      <RouteWithLayout
        path={routes.consultPolicy}
        component={Certificate.ConsultPolicy}
        layout={JourneyStepsLayout}
      />

      <RouteWithLayout
        path={routes.myLgpd.dashboard}
        component={MyLgpd.Dashboard}
        somePermissions={[
          'view_dashboard',
          'view_dashboard_gap',
          'view_dashboard_process',
          'view_dashboard_rnc',
          'stats_kanban_task',
        ]}
        layout={MainLayout}
        auth
      />
      <RouteWithLayout
        path={routes.myLgpd.home}
        component={MyLgpd.Home}
        layout={MainLayout}
        auth
      />
      <RouteWithLayout
        provider={[DataProcessOptionsProvider, AssetInventoryProvider]}
        path={routes.dataProcess.all}
        component={DataProcesses.Main}
        somePermissions={[
          permission.DATA_PROCESSES.LIST,
          permission.DATA_PROCESSES.CREATE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.audits.all}
        component={Audits.Main}
        somePermissions={[
          permission.AUDITS.VIEW,
          permission.AUDITS.MANAGE,
          permission.AUDITS.FINALIZE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.audits.show}
        component={Audits.Show}
        somePermissions={[
          permission.AUDITS.VIEW,
          permission.AUDITS.MANAGE,
          permission.AUDITS.FINALIZE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.audits.new}
        component={Audits.New}
        permission={permission.AUDITS.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.audits.edit}
        component={Audits.Edit}
        permission={permission.AUDITS.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.audits.automation}
        component={Audits.Automation}
        permission={permission.AUDITS.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.dataProcess.resume}
        component={DataProcesses.Resume}
        layout={MainLayout}
        somePermissions={[
          permission.DATA_PROCESSES.LIST,
          permission.DATA_PROCESSES.RESUME,
          permission.DATA_PROCESSES.CREATE,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        provider={[DataProcessOptionsProvider, AssetInventoryProvider]}
        path={routes.dataProcess.suggested}
        component={DataProcesses.Suggested}
        layout={MainLayout}
        permission={permission.DATA_PROCESSES.LIST_AND_MANAGE_SUGGESTED}
        exact
        auth
      />
      <RouteWithLayout
        provider={DataProcessOptionsProvider}
        path={routes.dataProcess.view}
        component={DataProcesses.Details}
        layout={MainLayout}
        somePermissions={[
          permission.DATA_PROCESSES.LIST,
          permission.AUDITS.MANAGE,
          permission.AUDITS.VIEW,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        provider={[DataProcessOptionsProvider, AssetInventoryProvider]}
        path={routes.dataProcess.suggestedChanges}
        component={DataProcesses.SuggestionChangesMain}
        layout={MainLayout}
        permission={permission.DATA_PROCESSES.CREATE}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.dataProcess.new}
        component={DataProcesses.New}
        provider={DataProcessProvider}
        layout={MainLayout}
        permission={permission.DATA_PROCESSES.CREATE}
        exact
        auth
      />
      <RouteWithLayout
        provider={DataProcessOptionsProvider}
        path={routes.dataProcess.edit}
        component={DataProcesses.EditPage}
        layout={MainLayout}
        permission={permission.DATA_PROCESSES.CREATE}
        exact
        auth
      />
      <RouteWithLayout
        provider={SuggestedChangeProvider}
        path={routes.dataProcess.viewSuggestions}
        component={DataProcesses.SuggestionChanges}
        layout={MainLayout}
        permission={permission.DATA_PROCESSES.CREATE}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.dataProcess.treatment.all}
        component={DataProcessTreatment.Main}
        layout={MainLayout}
        somePermissions={[
          permission.DATA_PROCESSES.LIST,
          permission.DATA_PROCESSES.CREATE,
          permission.DATA_PROCESS_TREATMENT.LIST,
          permission.DATA_PROCESS_TREATMENT.MANAGE,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.dataProcess.treatment.manage}
        component={DataProcessTreatment.Manage}
        layout={MainLayout}
        somePermissions={[
          permission.DATA_PROCESS_TREATMENT.MANAGE,
          permission.DATA_PROCESSES.CREATE,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.department.all}
        component={Departments.Main}
        layout={MainLayout}
        permission="list_departments"
        exact
        auth
      />
      <RouteWithLayout
        path={routes.department.new}
        component={Departments.New}
        layout={MainLayout}
        permission="create_department"
        exact
        auth
      />
      <RouteWithLayout
        path={routes.department.edit}
        component={Departments.Edit}
        layout={MainLayout}
        permission={permission.DEPARTMENTS.CREATE}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.committees.all}
        component={Committes.Main}
        somePermissions={[
          permission.COMMITTEES.LIST,
          permission.COMMITTEES.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.committees.show}
        component={Committes.Show}
        somePermissions={[
          permission.COMMITTEES.LIST,
          permission.COMMITTEES.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.committees.committeeUserNew}
        component={Committes.CommitteeUsersNew}
        permission={permission.COMMITTEES.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.committees.committeeUserEdit}
        component={Committes.CommitteeUsersEdit}
        permission={permission.COMMITTEES.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.externalPlatformIntegrations.all}
        component={ExternalPlatformIntegrations.Main}
        somePermissions={[
          permission.EXTERNAL_PLATFORM_INTEGRATIONS.LIST,
          permission.EXTERNAL_PLATFORM_INTEGRATIONS.MANAGE,
          permission.EXTERNAL_PLATFORM_INTEGRATIONS.UPDATE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.privacyPortal.tickets.all}
        component={DataSubjectTickets.Main}
        layout={MainLayout}
        somePermissions={[
          permission.TITULAR_TICKETS.LIST,
          permission.TITULAR_TICKETS.INTERACT,
          permission.TITULAR_TICKETS.MANAGE_CONFIGURATIONS,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.privacyPortal.tickets.details}
        component={DataSubjectTickets.Details}
        layout={MainLayout}
        somePermissions={[
          permission.TITULAR_TICKETS.LIST,
          permission.TITULAR_TICKETS.INTERACT,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.privacyPortal.tickets.config}
        component={DataSubjectTickets.Config}
        layout={MainLayout}
        permission={permission.TITULAR_TICKETS.MANAGE_CONFIGURATIONS}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.user.all}
        component={Users.Main}
        layout={MainLayout}
        permission="list_users"
        exact
        auth
      />
      <RouteWithLayout
        path={routes.user.new}
        component={Users.New}
        layout={MainLayout}
        permission="create_user"
        exact
        auth
      />
      <RouteWithLayout
        path={routes.user.edit}
        component={Users.Edit}
        layout={MainLayout}
        permission="edit_user"
        exact
        auth
      />
      <RouteWithLayout
        path={routes.settings}
        component={Settings.SettingsMain}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.documents.all}
        component={Documents.Main}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.documents.folders.show}
        component={Documents.FolderMain}
        layout={MainLayout}
        provider={DocumentGenerateProvider}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.documents.new}
        component={Documents.New}
        layout={MainLayout}
        permission={permission.PRIVACY_POLICIES.CREATE}
        provider={ConsentProvider}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.documents.show}
        component={Documents.Show}
        layout={MainLayout}
        somePermissions={[
          permission.PRIVACY_POLICIES.LIST,
          permission.PRIVACY_POLICIES.CREATE,
        ]}
        provider={ConsentProvider}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.documents.edit}
        component={Documents.Edit}
        layout={MainLayout}
        permission={permission.PRIVACY_POLICIES.CREATE}
        provider={ConsentProvider}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.documents.comparator}
        component={Documents.Comparator}
        layout={MainLayout}
        permission={permission.PRIVACY_POLICIES.CREATE}
        provider={ConsentProvider}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.documents.generate}
        component={DocumentTemplates.Generate}
        layout={MainLayout}
        provider={DocumentGenerateProvider}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.questionnaire.all}
        component={Questionnaires.Main}
        layout={MainLayout}
        reduceMargin
        exact
        auth
      />
      <RouteWithLayout
        path={routes.dponetQuestionnaire.reply}
        component={Questionnaires.DponetReply}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.supplierQuestionnaire.reply}
        component={Questionnaires.SuppliersReply}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.companyFragilities.all}
        component={CompanyFragilities.CompanyFragilityMain}
        layout={MainLayout}
        somePermissions={[
          permission.COMPANY_FRAGILITIES.LIST_COMPANY_FRAGILITIES,
          permission.COMPANY_FRAGILITIES.LIST_COMPANY_FRAGILITIES_BY_DEPARTMENT,
          permission.COMPANY_FRAGILITIES.MANAGE_COMPANY_FRAGILITIES,
          permission.COMPANY_FRAGILITIES
            .MANAGE_COMPANY_FRAGILITIES_BY_DEPARTMENT,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.companyFragilities.new}
        provider={CompanyFragilityProvider}
        component={CompanyFragilities.CompanyFragilityNew}
        layout={MainLayout}
        somePermissions={[
          permission.COMPANY_FRAGILITIES.MANAGE_COMPANY_FRAGILITIES,
          permission.COMPANY_FRAGILITIES
            .MANAGE_COMPANY_FRAGILITIES_BY_DEPARTMENT,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.companyFragilities.edit}
        provider={CompanyFragilityProvider}
        component={CompanyFragilities.CompanyFragilityEdit}
        layout={MainLayout}
        somePermissions={[
          permission.COMPANY_FRAGILITIES.MANAGE_COMPANY_FRAGILITIES,
          permission.COMPANY_FRAGILITIES
            .MANAGE_COMPANY_FRAGILITIES_BY_DEPARTMENT,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.incidents.all}
        component={Incidents.Main}
        layout={MainLayout}
        exact
        somePermissions={[
          permission.INCIDENTS.LIST,
          permission.INCIDENTS.CREATE,
          permission.INCIDENTS.MANAGE,
        ]}
        auth
      />
      <RouteWithLayout
        path={routes.phishing.dashboard}
        component={Phishing.Dashboard}
        layout={MainLayout}
        exact
        auth
        somePermissions={[
          permission.PHISHING.DASHBOARD,
          permission.PHISHING.MANAGE,
        ]}
      />
      <RouteWithLayout
        path={routes.phishing.all}
        component={Phishing.Main}
        layout={MainLayout}
        exact
        auth
        somePermissions={[permission.PHISHING.LIST, permission.PHISHING.MANAGE]}
      />
      <RouteWithLayout
        path={routes.phishing.new}
        component={Phishing.New}
        provider={PhishingProvider}
        layout={MainLayout}
        exact
        auth
        permission={permission.PHISHING.MANAGE}
      />
      <RouteWithLayout
        path={routes.phishing.edit}
        component={Phishing.Edit}
        provider={PhishingProvider}
        layout={MainLayout}
        exact
        auth
        permission={permission.PHISHING.MANAGE}
      />
      <RouteWithLayout
        path={routes.phishing.show}
        component={Phishing.Show}
        provider={PhishingProvider}
        layout={MainLayout}
        exact
        auth
        somePermissions={[permission.PHISHING.LIST, permission.PHISHING.MANAGE]}
      />
      <RouteWithLayout
        path={routes.incidents.show}
        provider={IncidentManegementProvider}
        component={IncidentManagements.Show}
        layout={MainLayout}
        exact
        somePermissions={[
          permission.INCIDENTS.LIST,
          permission.INCIDENTS.CREATE,
          permission.INCIDENTS.MANAGE,
        ]}
        auth
      />
      <RouteWithLayout
        path={routes.incidents.edit}
        provider={IncidentManegementProvider}
        component={IncidentManagements.Edit}
        layout={MainLayout}
        exact
        somePermissions={[
          permission.INCIDENTS.CREATE,
          permission.INCIDENTS.MANAGE,
        ]}
        auth
      />
      <RouteWithLayout
        path={routes.incidents.new}
        provider={IncidentManegementProvider}
        component={IncidentManagements.New}
        layout={MainLayout}
        auth
        somePermissions={[
          permission.INCIDENTS.CREATE,
          permission.INCIDENTS.MANAGE,
        ]}
      />
      <RouteWithLayout
        path={routes.privacyPortal.stamps}
        component={PrivacyStamps}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.profiles.all}
        component={Profiles.Main}
        layout={MainLayout}
        exact
        permission={permission.PROFILES.LIST}
        auth
      />
      <RouteWithLayout
        path={routes.profiles.new}
        component={Profiles.New}
        layout={MainLayout}
        exact
        permission={permission.PROFILES.CREATE}
        auth
      />
      <RouteWithLayout
        path={routes.profiles.edit}
        component={Profiles.Edit}
        layout={MainLayout}
        exact
        permission={permission.PROFILES.EDIT}
        auth
      />
      <RouteWithLayout
        path={routes.marketplace.all}
        component={Marketplace.Main}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.marketplace.services}
        component={Marketplace.Services}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.marketplace.segments}
        component={Marketplace.Segments}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.marketplace.show}
        component={Marketplace.Show}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.marketplace.orders}
        component={Marketplace.Orders}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.marketplace.products}
        component={Marketplace.ProductsAndServices}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.subscription.default}
        component={Subscription}
        layout={PortalLayout}
        provider={SubscriptionProvider}
        exact
        redirect={false}
      />
      <RouteWithLayout
        path={routes.subscription.trial}
        component={SubscriptionCompany.Main}
        layout={CheckoutLayout}
        provider={SubscriptionProvider}
        exact
        redirect={false}
      />
      <RouteWithLayout
        path={routes.subscription.checkoutPayment}
        component={CheckoutPayment}
        layout={PortalLayout}
        provider={CheckoutPaymentProvider}
        reduceMargin
        exact
        redirect={false}
      />
      <RouteWithLayout
        path={routes.subscription.paymentLink}
        component={PaymentLink}
        provider={CheckoutPaymentProvider}
        exact
        redirect={false}
      />
      <RouteWithLayout
        path={routes.reports.management}
        component={Reports.MainManagement}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.control}
        component={Reports.MainControlReport}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.fragility}
        component={Reports.MainFragilityReport}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.acceptance}
        component={Reports.AcceptanceReport}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.ripd}
        component={Reports.MainRipd}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.ropa}
        component={Reports.MainRopa}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.accountability}
        component={Reports.MainAccountability}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.validate}
        component={Reports.ValidateReport}
        layout={JourneyStepsLayout}
      />
      <RouteWithLayout
        path={routes.reports.trainingReport}
        component={Reports.TrainingReport}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.lia}
        component={Reports.MainLia}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.consent}
        component={Reports.MainConsent}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.cookiesConsents}
        component={Reports.MainCookiesConsentsReport}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.cookiesTags}
        component={Reports.MainCookiesTagsReport}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.phishing}
        component={Reports.MainPhishingReport}
        layout={MainLayout}
        permission={permission.PHISHING.REPORT}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.audit}
        component={Reports.MainAuditReport}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.partners}
        component={Reports.MainPartnersReport}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.incidents}
        component={Reports.IncidentsReports}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.assetInventory}
        component={Reports.AssetInventory}
        somePermissions={[permission.ASSET_INVENTORY.SHOW]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.privacyByDesign}
        component={Reports.MainPrivacyByDesignReport}
        permission={permission.RIPD.EXPORT}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.reports.kanbanTasks}
        component={Reports.ActivitiesReport}
        somePermissions={[
          permission.KANBAN_TASKS.LIST,
          permission.KANBAN_TASKS.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.cookies.banners}
        component={Cookies.Banners}
        layout={MainLayout}
        provider={CookiesProvider}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.cookies.settings}
        component={Cookies.Settings}
        provider={CookiesProvider}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.cookies.consents}
        component={Cookies.Consents}
        provider={CookiesProvider}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.lastUpdates.all}
        component={LastUpdates}
        permission={permission.LAST_UPDATE.LIST}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.faqsDocumentations.all}
        component={FaqsDocumentations.main}
        permission={permission.FAQS_DOCUMENTATIONS.LIST}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.faqsDocumentations.show}
        component={FaqsDocumentations.show}
        permission={permission.FAQS_DOCUMENTATIONS.LIST}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.consent.all}
        component={Consent.ConsentMain}
        permission={permission.CONSENTS.LIST}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.consent.new}
        component={Consent.ConsentNew}
        permission={permission.CONSENTS.CREATE}
        provider={ConsentProvider}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.consent.consentEmailConfirmed}
        component={Consent.ConsentEmailConfirmed}
      />
      <RouteWithLayout
        path={routes.consent.edit}
        component={Consent.ConsentEdit}
        permission={permission.CONSENTS.CHANGE_RESPONSIBLE}
        provider={ConsentProvider}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.consent.show}
        component={Consent.ConsentShow}
        permission={permission.CONSENTS.SHOW}
        provider={ConsentProvider}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.consent.consentReceipt}
        component={Consent.ConsentReceiptMain}
        permission={permission.CONSENT_RECEIPTS.LIST}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.consent.consentReceiptNew}
        component={Consent.ConsentReceiptNew}
        permission={permission.CONSENTS.CREATE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.consent.consentReceiptDetails}
        component={Consent.ConsentReceiptDetails}
        permission={permission.CONSENTS.SHOW}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.lgpd.show}
        component={TechnicalFaqs.LGPDShow}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.lgpd.main}
        component={TechnicalFaqs.LGPDMain}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.lgpd.specificShow}
        component={TechnicalFaqs.LGPDShow}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.lgpd.ticket}
        component={TechnicalFaqs.PersonalizedService}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.informationSecurity.main}
        component={TechnicalFaqs.InformationSecurityMain}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.informationSecurity.show}
        component={TechnicalFaqs.InformationSecurityShow}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.informationSecurity.specificShow}
        component={TechnicalFaqs.InformationSecurityShow}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.informationSecurity.ticket}
        component={TechnicalFaqs.PersonalizedService}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.tickets.all}
        component={TechnicalFaqs.Ticket}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.tickets.details}
        component={TechnicalFaqs.TicketDetails}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.contracts.ticket}
        component={TechnicalFaqs.ContractMain}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.technicalFaqs.thirdPartyAssessment.ticket}
        component={TechnicalFaqs.ThirdPartyAssessmentMain}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.questionControls.all}
        component={QuestionControls.Main}
        permission={permission.DIAGNOSTICS.LIST}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.questionControls.edit}
        component={QuestionControls.Edit}
        permission={permission.DIAGNOSTICS.UPDATE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.questionControls.show}
        component={QuestionControls.Show}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.questionControls.new}
        component={QuestionControls.New}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.questionControls.all}
        component={QuestionControls.Main}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.questionControls.edit}
        component={QuestionControls.Edit}
        permission={permission.DIAGNOSTICS.UPDATE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.questionControls.show}
        component={QuestionControls.Show}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.questionControls.show}
        component={QuestionControls.Show}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.complaint.all}
        component={Complaint.Main}
        somePermissions={[
          permission.COMPLAINT_MESSAGES.CREATE,
          permission.COMPLAINTS.ACTIVATE_COMPLAINTS,
          permission.COMPLAINTS.LIST_CONFIGURATIONS,
          permission.COMPLAINTS.LIST,
          permission.COMPLAINTS.MANAGE_CONFIGURATIONS,
          permission.COMPLAINTS.REGISTER_RESPONSIBLE,
          permission.COMPLAINTS.SHOW,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.complaint.show}
        component={Complaint.Show}
        somePermissions={[
          permission.COMPLAINT_MESSAGES.CREATE,
          permission.COMPLAINTS.ACTIVATE_COMPLAINTS,
          permission.COMPLAINTS.LIST_CONFIGURATIONS,
          permission.COMPLAINTS.LIST,
          permission.COMPLAINTS.MANAGE_CONFIGURATIONS,
          permission.COMPLAINTS.REGISTER_RESPONSIBLE,
          permission.COMPLAINTS.SHOW,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.complaint.config}
        component={Complaint.Config}
        somePermissions={[
          permission.COMPLAINTS.ACTIVATE_COMPLAINTS,
          permission.COMPLAINTS.LIST_CONFIGURATIONS,
          permission.COMPLAINTS.MANAGE_CONFIGURATIONS,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.complaint.categoryEdit}
        component={Complaint.CategoryEdit}
        somePermissions={[
          permission.COMPLAINTS.ACTIVATE_COMPLAINTS,
          permission.COMPLAINTS.MANAGE_CONFIGURATIONS,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.privacyPortal.settings}
        component={PrivacyPortal.Settings}
        layout={MainLayout}
        somePermissions={[
          permission.PRIVACY_PORTAL_THEMES.SHOW,
          permission.PRIVACY_PORTAL_THEMES.MANAGE,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.thirdPartyManagements.mySuppliers.all}
        component={ThirdPartyManagements.MySuppliers.All}
        somePermissions={[
          permission.COMPANY_SUPPLIERS.SHOW,
          permission.COMPANY_SUPPLIERS.UPDATE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.thirdPartyManagements.mySuppliers.new}
        component={ThirdPartyManagements.MySuppliers.New}
        permission={permission.COMPANY_SUPPLIERS.UPDATE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.thirdPartyManagements.mySuppliers.show}
        component={ThirdPartyManagements.MySuppliers.Show}
        somePermissions={[
          permission.COMPANY_SUPPLIERS.SHOW,
          permission.COMPANY_SUPPLIERS.UPDATE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.thirdPartyManagements.mySuppliers.edit}
        component={ThirdPartyManagements.MySuppliers.Edit}
        permission={permission.COMPANY_SUPPLIERS.UPDATE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.thirdPartyManagements.mySuppliers.audit.show}
        component={ThirdPartyManagements.MySuppliers.AuditShow}
        somePermissions={[
          permission.COMPANY_SUPPLIERS.SHOW,
          permission.COMPANY_SUPPLIERS.UPDATE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.thirdPartyManagements.mySuppliers.audit.new}
        component={ThirdPartyManagements.MySuppliers.AuditNew}
        permission={permission.COMPANY_SUPPLIERS.UPDATE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.thirdPartyManagements.mySuppliers.audit.edit}
        component={ThirdPartyManagements.MySuppliers.AuditEdit}
        permission={permission.COMPANY_SUPPLIERS.UPDATE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.thirdPartyManagements.questionnairesReceived}
        component={ThirdPartyManagements.QuestionnairesReceived}
        permission={permission.COMPANY_SUPPLIER_QUESTIONNAIRES_RECEIVED.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.thirdPartyManagements.preAvaliations}
        component={ThirdPartyManagements.PreAvaliation}
        somePermissions={[
          permission.COMPANY_SUPPLIERS.SHOW,
          permission.COMPANY_SUPPLIERS.UPDATE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.thirdPartyManagements.suggestions}
        component={ThirdPartyManagements.Suggestion}
        permission={permission.SUPPLIER_SUGGESTIONS.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.governanceProjects.all}
        component={GovernanceProjects.Main}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.governanceProjects.manage}
        component={GovernanceProjects.Manage}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.governanceProjects.activity.manage}
        component={GovernanceProjects.ActivityManage}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.governanceProjectStages.dpo}
        component={GovernanceProjectStages.NameDpo}
        provider={GovernanceProjectNameDpoProvider}
        layout={JourneyWhiteLabel}
        exact
      />
      <RouteWithLayout
        provider={[DataProcessOptionsProvider, AssetInventoryProvider]}
        path={routes.dataIntelligence}
        component={DataIntelligence.Main}
        layout={MainLayout}
        somePermissions={[
          permission.DATA_PROCESSES.LIST,
          permission.DATA_PROCESSES.RESUME,
          permission.DATA_PROCESSES.CREATE,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.helpCenter.all}
        component={HelpCenter.Main}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.helpCenter.solicitation}
        component={HelpCenter.Solicitation}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.assetInventory.all}
        component={AssetInventory.Main}
        provider={AssetInventoryProvider}
        somePermissions={[
          permission.ASSET_INVENTORY.SHOW,
          permission.ASSET_INVENTORY.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.assetInventory.new}
        component={AssetInventory.New}
        provider={AssetInventoryProvider}
        permission={permission.ASSET_INVENTORY.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.assetInventory.show}
        component={AssetInventory.Show}
        provider={AssetInventoryProvider}
        somePermissions={[
          permission.ASSET_INVENTORY.SHOW,
          permission.ASSET_INVENTORY.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.assetInventory.edit}
        component={AssetInventory.Edit}
        provider={AssetInventoryProvider}
        permission={permission.ASSET_INVENTORY.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.aiInventory.all}
        component={AiInventory.Main}
        layout={MainLayout}
        somePermissions={[
          permission.AI_INVENTORY.LIST,
          permission.AI_INVENTORY.SHOW,
          permission.AI_INVENTORY.MANAGE,
          permission.AI_INVENTORY.MANAGE_GUEST_PORTAL,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.aiInventory.daiNew}
        component={AiInventory.DaiRegistration}
        layout={MainLayout}
        permission={permission.AI_INVENTORY.MANAGE}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.aiInventory.new}
        component={AiInventory.New}
        layout={MainLayout}
        permission={permission.AI_INVENTORY.MANAGE}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.aiInventory.show}
        component={AiInventory.Show}
        layout={MainLayout}
        somePermissions={[
          permission.AI_INVENTORY.LIST,
          permission.AI_INVENTORY.SHOW,
          permission.AI_INVENTORY.MANAGE,
          permission.AI_INVENTORY.MANAGE_GUEST_PORTAL,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.aiInventory.edit}
        component={AiInventory.Edit}
        layout={MainLayout}
        permission={permission.AI_INVENTORY.MANAGE}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.aiInventory.indicators}
        component={AiInventory.Indicators}
        layout={MainLayout}
        somePermissions={[
          permission.AI_INVENTORY.LIST,
          permission.AI_INVENTORY.MANAGE,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.trainingCourses.all}
        component={TrainingCourses.Main}
        layout={MainLayout}
        somePermissions={[
          permission.NEW_PLATFORM_COURSE.CONFIG,
          permission.NEW_PLATFORM_COURSE.SHOW,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.trainingCourses.manage}
        component={TrainingCourses.Manage}
        layout={MainLayout}
        somePermissions={[
          permission.NEW_PLATFORM_COURSE.CONFIG,
          permission.NEW_PLATFORM_COURSE.SHOW,
        ]}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.privacyByDesign.all}
        component={PrivacyByDesign.Main}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.privacyByDesign.new}
        component={PrivacyByDesign.New}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.privacyByDesign.show}
        component={PrivacyByDesign.Show}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.privacyByDesign.edit}
        component={PrivacyByDesign.Edit}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.kpi.all}
        component={Kpi.Main}
        somePermissions={[
          permission.MONITORING_MANAGEMENT_KPI.LIST,
          permission.MONITORING_MANAGEMENT_KPI.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.kpi.new}
        component={Kpi.New}
        permission={permission.MONITORING_MANAGEMENT_KPI.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.kpi.edit}
        component={Kpi.Edit}
        permission={permission.MONITORING_MANAGEMENT_KPI.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.kpi.show}
        somePermissions={[
          permission.MONITORING_MANAGEMENT_KPI.LIST,
          permission.MONITORING_MANAGEMENT_KPI.MANAGE,
        ]}
        component={Kpi.Show}
        layout={MainLayout}
        exact
        auth
      />

      <RouteWithLayout
        path={routes.pmc.all}
        component={Pmc.Main}
        somePermissions={[
          permission.MONITORING_MANAGEMENT_PMC.LIST,
          permission.MONITORING_MANAGEMENT_PMC.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.pmc.new}
        component={Pmc.New}
        permission={permission.MONITORING_MANAGEMENT_PMC.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.pmc.edit}
        component={Pmc.Edit}
        permission={permission.MONITORING_MANAGEMENT_PMC.MANAGE}
        layout={MainLayout}
        exact
        auth
      />

      <RouteWithLayout
        path={routes.pmc.show}
        component={Pmc.Show}
        somePermissions={[
          permission.MONITORING_MANAGEMENT_PMC.LIST,
          permission.MONITORING_MANAGEMENT_PMC.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.changesManagement.all}
        component={ChangesManagement.Main}
        somePermissions={[
          permission.CHANGES_MANAGEMENT.LIST,
          permission.CHANGES_MANAGEMENT.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.changesManagement.new}
        component={ChangesManagement.New}
        permission={permission.CHANGES_MANAGEMENT.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.changesManagement.show}
        component={ChangesManagement.Show}
        somePermissions={[
          permission.CHANGES_MANAGEMENT.LIST,
          permission.CHANGES_MANAGEMENT.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.changesManagement.edit}
        component={ChangesManagement.Edit}
        permission={permission.CHANGES_MANAGEMENT.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.activitiesPanel.all}
        component={ActivitiesPanel.Main}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.activitiesPanel.edit}
        component={ActivitiesPanel.ActivityEdit}
        layout={MainLayout}
        exact
        auth
        somePermissions={[
          permission.KANBAN_TASKS.MANAGE,
          permission.KANBAN_TASKS.FINALIZE,
        ]}
      />
      <RouteWithLayout
        path={routes.activitiesPanel.show}
        component={ActivitiesPanel.ActivityShow}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.activitiesPanel.notificationSettings}
        component={ActivitiesPanel.NotificationSettings}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.pdca.all}
        component={Pdca.Main}
        somePermissions={[
          permission.MONITORING_MANAGEMENT_PDCA.LIST,
          permission.MONITORING_MANAGEMENT_PDCA.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.pdca.new}
        component={Pdca.New}
        permission={permission.MONITORING_MANAGEMENT_PDCA.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.pdca.edit}
        component={Pdca.Edit}
        permission={permission.MONITORING_MANAGEMENT_PDCA.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.pdca.show}
        permission={permission.MONITORING_MANAGEMENT_PDCA.MANAGE}
        component={Pdca.Show}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.pdca.newCycle}
        permission={permission.MONITORING_MANAGEMENT_PDCA.MANAGE}
        component={PdcaCycle.New}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.pdca.editCycle}
        permission={permission.MONITORING_MANAGEMENT_PDCA.MANAGE}
        component={PdcaCycle.Edit}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.pdca.showCycle}
        permission={permission.MONITORING_MANAGEMENT_PDCA.MANAGE}
        component={PdcaCycle.Show}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.nc.all}
        component={Nc.Main}
        somePermissions={[
          permission.MONITORING_MANAGEMENT_NC.LIST,
          permission.MONITORING_MANAGEMENT_NC.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.nc.new}
        component={Nc.New}
        permission={permission.MONITORING_MANAGEMENT_NC.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.nc.edit}
        component={Nc.Edit}
        permission={permission.MONITORING_MANAGEMENT_NC.MANAGE}
        layout={MainLayout}
        exact
        auth
      />
      <RouteWithLayout
        path={routes.nc.show}
        component={Nc.Show}
        somePermissions={[
          permission.MONITORING_MANAGEMENT_NC.LIST,
          permission.MONITORING_MANAGEMENT_NC.MANAGE,
        ]}
        layout={MainLayout}
        exact
        auth
      />

      {/* Para novas rotas criar acima ↑*/}
      {/* Rotas sem autenticação */}
      <Route
        render={() => <NotFound useAuthHook={useAuth} />}
        exact
        path={routes.notFound}
      />
      <Route component={Forbidden} exact path={routes.forbidden} />
      <Route component={ServerError} exact path={routes.serverError} />
      <Route
        exact
        path="/"
        render={() => (
          <Redirect
            to={reverse(routes.root, {
              slug: constants.settings.PLATFORM_SLUG_ID,
            })}
          />
        )}
      />
      <RouteWithLayout
        path={routes.root}
        component={Login}
        layout={AuthLayout}
        provider={ClientPortalThemeProvider}
        exact
      />
      <RouteWithLayout
        path={routes.outsourcedDpoLogin}
        component={IntegrationLogin}
        provider={ClientPortalThemeProvider}
        layout={AuthLayout}
        exact
      />
      <RouteWithLayout
        path={routes.dpoEasyLogin}
        component={IntegrationLogin}
        provider={ClientPortalThemeProvider}
        layout={AuthLayout}
        exact
      />
      <RouteWithLayout
        path={routes.twoFactorAuthentication}
        component={TwoFactorAuthentication.Main}
        provider={ClientPortalThemeProvider}
        layout={AuthLayout}
        exact
      />
      <RouteWithLayout
        path={routes.forgotPassword}
        component={ForgotPassword}
        provider={ClientPortalThemeProvider}
        layout={AuthLayout}
        exact
      />
      <RouteWithLayout
        path={routes.newPassword}
        component={NewPassword}
        provider={ClientPortalThemeProvider}
        layout={AuthLayout}
        exact
      />
      <RouteWithLayout
        path={routes.login.microsoft}
        component={MicrosoftLogin}
        provider={ClientPortalThemeProvider}
        layout={AuthLayout}
        exact
      />
      <RouteWithLayout
        path={routes.loginMicrosoft}
        component={MicrosoftLogin}
        provider={ClientPortalThemeProvider}
        layout={AuthLayout}
        exact
      />
      <RouteWithLayout
        path={routes.ssoError}
        component={LoginSSOError}
        layout={AuthLayout}
        provider={ClientPortalThemeProvider}
        exact
      />
      <RouteWithLayout
        path={routes.sso}
        component={LoginSSO}
        layout={AuthLayout}
        provider={ClientPortalThemeProvider}
        exact
      />
      {/* <RouteWithLayout
        path={routes.loginTransparentIntegration}
        component={LoginTransparentIntegration}
        provider={ClientPortalThemeProvider}
        layout={AuthLayout}
        exact
      /> */}
      <Redirect to={routes.notFound} />
    </Switch>
  )
}

export const routes = {
  root: '/:slug?/',

  forgotPassword: '/:slug/forgot',

  outsourcedDpoLogin:
    '/:slug?/outsourced-dpo/:authToken/:companyId/:cookieToken',
  dpoEasyLogin: '/:slug?/dpo-easy/:authToken/:cookieToken',

  login: include('/:slug/login', {
    main: '',
    microsoft: 'microsoft',
    ssoCallback: 'sso_callback',
  }),

  // redirectUri do MSAL (authLogin.js) volta sem slug; precisa casar esta rota
  loginMicrosoft: '/login/microsoft',

  // success_url do SSO corporativo (api: sso_corporate_groups#redirect) → /:slug/sso
  sso: '/:slug/sso',

  // failure_url do SSO corporativo (api: sso_corporate_groups#redirect) → /:slug/sso/error
  ssoError: '/:slug/sso/error',

  // loginTransparentIntegration: '/login/:integrationToken/:publicToken',

  twoFactorAuthentication: '/:slug/two-factor-authentication/:accessToken',

  selectCompany: '/select-company',

  newPassword: '/:slug/new-password/:reset_password_token?',

  userSteps: '/user-steps',

  supplierStep: '/supplier-step',

  userTrailSteps: '/user-trails',

  myLgpd: include('/my-lgpd', {
    dashboard: 'dashboard',
    home: 'home',
  }),

  supplierInvite: '/supplier-invite/:companyToken',

  dataProcess: include('/data-processes', {
    all: '',
    view: 'show/:dataProcessId',
    new: 'new',
    edit: ':dataProcessId/edit',
    resume: 'resume',
    suggested: 'suggested',
    suggestedChanges: 'suggestions',
    viewSuggestions: ':dataProcessId/suggestions',
    treatment: include('treatment', {
      all: '',
      manage: ':dataProcessTreatmentId/manage',
    }),
  }),

  audits: include('/audits', {
    all: '',
    new: 'new',
    show: ':auditId/show',
    edit: ':auditId/edit',
    automation: ':auditConfigId/automation',
  }),

  department: include('/departments', {
    all: '',
    new: 'new',
    edit: ':departmentId/edit',
  }),

  committees: include('/committees', {
    all: '',
    show: ':committeeId/show',
    committeeUserNew: ':committeeId/commiteeUser/new',
    committeeUserEdit: ':committeeId/committeeUser/:committeeUserId/edit',
  }),

  externalPlatformIntegrations: include('/external-platform-integrations', {
    all: '',
  }),

  user: include('/users', {
    all: '',
    new: 'new',
    edit: ':userId/edit',
    account: '/account',
  }),

  settings: '/settings',

  documents: include('/documents', {
    all: '',
    new: 'new',
    generate: 'generate/:documentTemplateId?',
    show: ':documentId/show',
    edit: ':documentId/edit',
    comparator: ':documentId/version-comparator',
    folders: include('folders', {
      show: ':folderId',
    }),
  }),

  marketplace: include('/marketplace', {
    all: '',
    services: 'services/:id',
    segments: 'segments/:id',
    products: 'products',
    show: 'show/:partnerServiceId',
    orders: 'orders',
  }),

  privacyPortal: include('/privacy-portal', {
    stamps: 'stamps',
    settings: 'settings',
    tickets: include('tickets', {
      all: '',
      details: ':ticketId/details',
      config: 'config',
    }),
  }),

  complaint: include('/complaint', {
    all: '',
    show: ':complaintId/show',
    config: 'config',
    categoryEdit: 'categories/:categoryId/edit',
  }),

  questionnaire: include('/questionnaires', {
    all: '',
  }),

  dponetQuestionnaire: include('/questionnaires/dponet', {
    reply: ':questionnaireId/reply',
  }),

  supplierQuestionnaire: include('/questionnaires/suppliers', {
    reply: ':questionnaireId/reply',
  }),

  companyFragilities: include('/fragilities', {
    all: '',
    new: 'new',
    edit: ':companyFragilityId/edit',
  }),

  incidents: include('/incidents', {
    all: '',
    new: 'new',
    show: ':incidentId/show',
    edit: ':incidentId/edit',
    editComplete: ':incidentId/edit_complete',
  }),

  profiles: include('/profiles', {
    all: '',
    new: 'new',
    edit: ':profileId/edit',
  }),

  subscription: include('/subscription', {
    default: '',
    trial: 'trial',
    checkoutPayment: ':contractToken',
    paymentLink: 'payment-link/:paymentLinkToken',
  }),

  reports: {
    accountability: '/accountability',
    all: '?chooseReport=true',
    audit: '/audit',
    consent: '/reports/consent',
    cookiesConsents: '/reports/cookiesConsents',
    cookiesTags: '/reports/cookiesTags',
    phishing: '/reports/phishing',
    control: '/control',
    incidents: '/reports/incidents',
    fragility: '/fragility',
    acceptance: '/acceptance',
    lia: '/reports/lia',
    management: '/management',
    reportRmc: '/rmcReport',
    ripd: '/ripd',
    ropa: '/ropa',
    trainingReport: '/reports/training',
    partners: '/partners',
    validate: '/reports/validate/:token',
    assetInventory: '/reports/asset-inventory',
    privacyByDesign: '/reports/privacy-by-design',
    kanbanTasks: '/reports/activities',
  },

  cookies: include('/cookies-banner', {
    settings: ':id/settings',
    banners: '',
    consents: 'consents',
  }),

  lastUpdates: include('/last-updates', {
    all: '',
  }),

  technicalFaqs: include('/technical-faqs', {
    all: '',
    lgpd: include('lgpd', {
      main: '',
      show: 'show/all',
      specificShow: 'show/:subCategoryId',
      ticket: 'ticket',
    }),
    informationSecurity: include('information-security', {
      main: '',
      show: 'show/all',
      specificShow: 'show/:subCategoryId',
      ticket: 'ticket',
    }),
    tickets: include('tickets', {
      all: '',
      details: ':ticketId/details',
    }),
    contracts: include('contracts', {
      ticket: 'ticket',
    }),
    thirdPartyAssessment: include('third-party-assessment', {
      ticket: 'ticket',
    }),
  }),

  consent: include('/consent', {
    all: '',
    new: 'new',
    show: ':consentFormId/show',
    edit: ':consentFormId/edit',
    consentReceipt: 'receipts',
    consentReceiptNew: 'receipts/new',
    consentReceiptDetails: ':consentFormId/receipts/:consentReceiptId',
    consentEmailConfirmed: 'consentEmailConfirmed',
  }),

  faqsDocumentations: include('/faqs-documentations', {
    all: '',
    show: 'show/:faqsDocumentationId',
  }),

  questionControls: include('/question-controls', {
    all: '',
    new: 'new',
    show: ':questionControlId/show',
    edit: ':questionControlId/edit',
  }),

  governanceProjects: include('/governance-projects', {
    all: '',
    manage: ':governanceProjectId/manage',
    activity: include(':governanceProjectId/activity', {
      manage: ':activityId/manage',
    }),
  }),

  governanceProjectStages: include('/:slug/governance-project', {
    dpo: ':templateToken/dpo',
  }),

  dataIntelligence: '/data-intelligence',

  assetInventory: include('/asset-inventory', {
    all: '',
    new: 'new',
    show: ':assetInventoryId/show',
    edit: ':assetInventoryId/edit',
  }),

  helpCenter: include('/help-center', {
    all: '',
    solicitation: 'solicitation',
  }),

  notFound: '/not-found',
  forbidden: '/forbidden',
  serverError: '/server-error',

  resume: '/resume',

  consultCertificate: '/certificate/:token',
  consultPolicy: '/certificate-policy/:token',

  trainningJourney: '/trainning/:trainningJourneyId',

  thirdPartyManagements: include('/third-party-managements', {
    mySuppliers: include('my-suppliers', {
      all: '',
      new: 'new',
      edit: ':companySupplierId/edit',
      show: ':companySupplierId/show',
      audit: {
        new: ':companySupplierId/audit/new',
        edit: ':companySupplierId/audit/:companySupplierAuditId/edit',
        show: ':companySupplierId/audit/:companySupplierAuditId/show',
      },
    }),
    questionnairesReceived: 'questionnaires-received',
    preAvaliations: 'pre-avaliations',
    suggestions: 'suggestions',
  }),

  aiInventory: include('/ai-inventory', {
    all: '',
    new: 'new',
    daiNew: 'new/dai',
    show: ':aiInventoryId/show',
    edit: ':aiInventoryId/edit',
    indicators: 'indicators',
  }),

  trainingCourses: include('/training-courses', {
    all: '',
    manage: ':courseId/manage',
  }),

  privacyByDesign: include('/privacy-by-design', {
    all: '',
    new: 'new',
    show: ':privacyByDesignId/show',
    edit: ':privacyByDesignId/edit',
  }),

  kpi: include('/monitoring-management/kpi', {
    all: '',
    new: 'new',
    show: ':monitoringManagementKpiId/show',
    edit: ':monitoringManagementKpiId/edit',
  }),

  pmc: include('/monitoring-management/pmc', {
    all: '',
    new: 'new',
    show: ':monitoringManagementPmcId/show',
    edit: ':monitoringManagementPmcId/edit',
  }),

  changesManagement: include('/changes-management', {
    all: '',
    new: 'new',
    show: ':changesManagementId/show',
    edit: ':changesManagementId/edit',
  }),

  activitiesPanel: include('/activities-panel', {
    all: '',
    show: ':id/show',
    edit: ':id/edit',
    notificationSettings: 'notification-settings',
  }),

  pdca: include('/monitoring-management-pdca', {
    all: '',
    new: 'new',
    show: ':pdcaId/show',
    edit: ':pdcaId/edit',
    newCycle: ':pdcaId/new-cycle',
    editCycle: ':pdcaId/edit-cycle/:pdcaCycleId',
    showCycle: ':pdcaId/show-cycle/:pdcaCycleId',
  }),

  nc: include('/monitoring-management/nc', {
    all: '',
    new: 'new',
    show: ':monitoringManagementNcId/show',
    edit: ':monitoringManagementNcId/edit',
  }),

  phishing: include('/phishing', {
    all: '',
    dashboard: 'dashboard',
    new: 'new',
    edit: ':phishingId/edit',
    show: ':phishingId/show',
  }),
}

export default Routes
